import { API_BASE, authHeaders } from "./api";

// ---------------------------------------------------------------------------
// Whoami — admin gate check
// Returns true if the current user is an admin (200 from /api/admin/whoami),
// false on any non-ok response or thrown error (network, auth, 404, etc.).
//
// The in-flight/resolved promise is memoized at module scope so concurrent
// callers (AppShell + AdminGuard both call useIsAdmin on the same page load)
// share ONE network request instead of racing two. Call _resetWhoamiCache()
// in tests that need a fresh state.
// ---------------------------------------------------------------------------

let _whoamiPromise: Promise<boolean> | null = null;

/** Exposed for testing only — reset the memoized promise. */
export function _resetWhoamiCache(): void {
  _whoamiPromise = null;
}

export function getWhoami(): Promise<boolean> {
  if (_whoamiPromise !== null) return _whoamiPromise;
  _whoamiPromise = (async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/admin/whoami`, {
        headers: await authHeaders(),
      });
      return resp.ok;
    } catch {
      return false;
    }
  })();
  return _whoamiPromise;
}

// ---------------------------------------------------------------------------
// Response types — used by overview / users / audit fetchers
// ---------------------------------------------------------------------------

export interface AdminOverview {
  user_count: number;
  transcript_count: number;
  audio_bytes: number;
  audio_duration_ms: number;
  spend_usd: number;
}

export interface AdminUserRow {
  user_id: string;
  email: string | null;
  joined_at: string | null;
  workspace_id: string | null;
  transcript_count: number;
  audio_bytes: number;
  audio_duration_ms: number;
  run_count: number;
  spend_usd: number;
  status: "active" | "blocked" | "pending";
}

export interface AdminUserDetail extends AdminUserRow {
  events_by_type: Record<string, number>;
}

export interface AdminAuditRow {
  id: string;
  created_at: string;
  event_type: string;
  actor_type: string;
  actor_email: string | null;
  meeting_id: string | null;
  cost_usd: number | null;
}

export interface AdminAuditParams {
  user_id?: string;
  event_type?: string;
  limit?: number;
  before?: string;
}

export interface OperatorEventRow {
  id: string;
  created_at: string;
  action: string;
  actor_email: string | null;
  target_email: string | null;
  detail: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Fetchers — throw on non-ok (exercised by the next task)
// ---------------------------------------------------------------------------

export async function getAdminOverview(): Promise<AdminOverview> {
  const resp = await fetch(`${API_BASE}/api/admin/overview`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`admin/overview failed: ${resp.status}`);
  return resp.json();
}

export async function getAdminUsers(): Promise<AdminUserRow[]> {
  const resp = await fetch(`${API_BASE}/api/admin/users`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`admin/users failed: ${resp.status}`);
  return resp.json();
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  const resp = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`admin/users/${userId} failed: ${resp.status}`);
  return resp.json();
}

export async function getAdminAudit(params?: AdminAuditParams): Promise<AdminAuditRow[]> {
  const qs = new URLSearchParams();
  if (params?.user_id) qs.set("user_id", params.user_id);
  if (params?.event_type) qs.set("event_type", params.event_type);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.before) qs.set("before", params.before);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const resp = await fetch(`${API_BASE}/api/admin/audit${query}`, {
    headers: await authHeaders(),
  });
  if (!resp.ok) throw new Error(`admin/audit failed: ${resp.status}`);
  return resp.json();
}

// ---------------------------------------------------------------------------
// Mutation helpers — user-action endpoints (invite, block, delete, etc.)
// ---------------------------------------------------------------------------

async function adminMutate(path: string, method: "POST" | "DELETE", body?: unknown): Promise<any> {
  const resp = await fetch(`${API_BASE}/api/admin/${path}`, {
    method,
    headers: {
      ...(await authHeaders()),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) {
    // Prefer the server's honest `detail` message (e.g. an email rate-limit
    // explanation) over a bare status code.
    let detail = "";
    try {
      const body = await resp.json();
      detail = typeof body?.detail === "string" ? body.detail : "";
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `admin/${path} failed: ${resp.status}`);
  }
  return resp.json();
}

export const inviteUser = (email: string) => adminMutate("users/invite", "POST", { email });
export const revokeInvite = (id: string) => adminMutate(`users/${encodeURIComponent(id)}/invite`, "DELETE");
export const blockUser = (id: string) => adminMutate(`users/${encodeURIComponent(id)}/block`, "POST");
export const unblockUser = (id: string) => adminMutate(`users/${encodeURIComponent(id)}/unblock`, "POST");
export const deleteUserPermanently = (id: string): Promise<{ ok: boolean; r2_delete_warning: string | null }> =>
  adminMutate(`users/${encodeURIComponent(id)}`, "DELETE");

export async function getOperatorEvents(): Promise<OperatorEventRow[]> {
  const resp = await fetch(`${API_BASE}/api/admin/operator-events`, { headers: await authHeaders() });
  if (!resp.ok) throw new Error(`admin/operator-events failed: ${resp.status}`);
  return resp.json();
}
